import { c as _c } from "react/compiler-runtime";
import { Box, Container, Link as MuiLink, Typography } from "@mui/material";
import type { InferGetStaticPropsType, NextPage } from "next";
import Link from "next/link";
import React from "react";
import { PrivacyCookieInventoryTable } from "#/features/privacy/ui/PrivacyCookieInventoryTable";
import { getI18nPropsWithCanonical } from "#/i18n/getI18nProps";
import { useI18nContext } from "#/shared/hooks";
import { SiteSeoHead } from "#/shared/ui/seo/SiteSeoHead";
import { MainLayout } from "#/shared/ui/templates/MainLayout";
export const getStaticProps = getI18nPropsWithCanonical("/privacy");
type PrivacyPageProps = InferGetStaticPropsType<typeof getStaticProps>;
const PrivacySection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mt: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <Typography variant="h5" component="h2" gutterBottom={true}>{title}</Typography>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <Box component="section" sx={t1}>{t2}{children}</Box>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const PrivacyParagraph: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mb: 2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Typography variant="body1" color="text.secondary" sx={t1}>{children}</Typography>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const PrivacyPage: NextPage<PrivacyPageProps> = t0 => {
  const $ = _c(199);
  const {
    canonicalUrl
  } = t0;
  const {
    LL
  } = useI18nContext();
  let t1;
  if ($[0] !== LL) {
    t1 = LL.PRIVACY_PAGE_TITLE();
    $[0] = LL;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${t1} — dStruct`;
  let t3;
  if ($[2] !== LL) {
    t3 = LL.PRIVACY_INTRO();
    $[2] = LL;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== canonicalUrl || $[5] !== t2 || $[6] !== t3) {
    t4 = <SiteSeoHead title={t2} description={t3} canonicalUrl={canonicalUrl} />;
    $[4] = canonicalUrl;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      py: 4
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== LL) {
    t6 = LL.PRIVACY_PAGE_TITLE();
    $[9] = LL;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <Typography variant="h4" component="h1" gutterBottom={true}>{t6}</Typography>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== LL) {
    t8 = LL.PRIVACY_INTRO();
    $[13] = LL;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <PrivacyParagraph>{t8}</PrivacyParagraph>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== LL) {
    t10 = LL.PRIVACY_LAST_UPDATED();
    $[17] = LL;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <PrivacyParagraph>{t10}</PrivacyParagraph>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== LL) {
    t12 = LL.PRIVACY_CONTROLLER_TITLE();
    $[21] = LL;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== LL) {
    t13 = LL.PRIVACY_CONTROLLER_BODY();
    $[23] = LL;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <PrivacyParagraph>{t13}</PrivacyParagraph>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== LL) {
    t15 = LL.PRIVACY_CONTACT_INTRO();
    $[27] = LL;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== LL) {
    t16 = LL.PRIVACY_CONTACT_EMAIL();
    $[29] = LL;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `mailto:${t16}`;
  let t18;
  if ($[31] !== LL) {
    t18 = LL.PRIVACY_CONTACT_EMAIL();
    $[31] = LL;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <MuiLink href={t17}>{t18}</MuiLink>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t15 || $[37] !== t19) {
    t20 = <PrivacyParagraph>{t15}{" "}{t19}.</PrivacyParagraph>;
    $[36] = t15;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t12 || $[40] !== t14 || $[41] !== t20) {
    t21 = <PrivacySection title={t12}>{t14}{t20}</PrivacySection>;
    $[39] = t12;
    $[40] = t14;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== LL) {
    t22 = LL.PRIVACY_DATA_COLLECTED_TITLE();
    $[43] = LL;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== LL) {
    t23 = LL.PRIVACY_DATA_COLLECTED_BODY();
    $[45] = LL;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t23) {
    t24 = <PrivacyParagraph>{t23}</PrivacyParagraph>;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t22 || $[50] !== t24) {
    t25 = <PrivacySection title={t22}>{t24}</PrivacySection>;
    $[49] = t22;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== LL) {
    t26 = LL.PRIVACY_LEGAL_BASES_TITLE();
    $[52] = LL;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== LL) {
    t27 = LL.PRIVACY_LEGAL_BASES_BODY();
    $[54] = LL;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t27) {
    t28 = <PrivacyParagraph>{t27}</PrivacyParagraph>;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t26 || $[59] !== t28) {
    t29 = <PrivacySection title={t26}>{t28}</PrivacySection>;
    $[58] = t26;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== LL) {
    t30 = LL.PRIVACY_RETENTION_TITLE();
    $[61] = LL;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== LL) {
    t31 = LL.PRIVACY_RETENTION_BODY();
    $[63] = LL;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t31) {
    t32 = <PrivacyParagraph>{t31}</PrivacyParagraph>;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t30 || $[68] !== t32) {
    t33 = <PrivacySection title={t30}>{t32}</PrivacySection>;
    $[67] = t30;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== LL) {
    t34 = LL.PRIVACY_RIGHTS_TITLE();
    $[70] = LL;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== LL) {
    t35 = LL.PRIVACY_RIGHTS_BODY();
    $[72] = LL;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== t35) {
    t36 = <PrivacyParagraph>{t35}</PrivacyParagraph>;
    $[74] = t35;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== LL) {
    t37 = LL.PRIVACY_WITHDRAW_CONSENT_BODY();
    $[76] = LL;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== t37) {
    t38 = <PrivacyParagraph>{t37}</PrivacyParagraph>;
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t34 || $[81] !== t36 || $[82] !== t38) {
    t39 = <PrivacySection title={t34}>{t36}{t38}</PrivacySection>;
    $[80] = t34;
    $[81] = t36;
    $[82] = t38;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== LL) {
    t40 = LL.PRIVACY_SUBPROCESSORS_TITLE();
    $[84] = LL;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== LL) {
    t41 = LL.PRIVACY_SUBPROCESSORS_BODY();
    $[86] = LL;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== t41) {
    t42 = <PrivacyParagraph>{t41}</PrivacyParagraph>;
    $[88] = t41;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== t40 || $[91] !== t42) {
    t43 = <PrivacySection title={t40}>{t42}</PrivacySection>;
    $[90] = t40;
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] !== LL) {
    t44 = LL.PRIVACY_TRANSFERS_TITLE();
    $[93] = LL;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== LL) {
    t45 = LL.PRIVACY_TRANSFERS_BODY();
    $[95] = LL;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t45) {
    t46 = <PrivacyParagraph>{t45}</PrivacyParagraph>;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== t44 || $[100] !== t46) {
    t47 = <PrivacySection title={t44}>{t46}</PrivacySection>;
    $[99] = t44;
    $[100] = t46;
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] !== LL) {
    t48 = LL.PRIVACY_COOKIES_SECTION_TITLE();
    $[102] = LL;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] !== LL) {
    t49 = LL.PRIVACY_COOKIES_OVERVIEW_BODY();
    $[104] = LL;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== t49) {
    t50 = <PrivacyParagraph>{t49}</PrivacyParagraph>;
    $[106] = t49;
    $[107] = t50;
  } else {
    t50 = $[107];
  }
  let t51;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = {
      mt: 3,
      mb: 1
    };
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] !== LL) {
    t52 = LL.PRIVACY_COOKIES_ESSENTIAL_TITLE();
    $[109] = LL;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] !== t52) {
    t53 = <Typography variant="h6" component="h3" sx={t51}>{t52}</Typography>;
    $[111] = t52;
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  let t54;
  if ($[113] !== LL) {
    t54 = LL.PRIVACY_COOKIES_ESSENTIAL_BODY();
    $[113] = LL;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] !== t54) {
    t55 = <PrivacyParagraph>{t54}</PrivacyParagraph>;
    $[115] = t54;
    $[116] = t55;
  } else {
    t55 = $[116];
  }
  let t56;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = {
      mt: 3,
      mb: 1
    };
    $[117] = t56;
  } else {
    t56 = $[117];
  }
  let t57;
  if ($[118] !== LL) {
    t57 = LL.PRIVACY_COOKIES_PREFERENCES_TITLE();
    $[118] = LL;
    $[119] = t57;
  } else {
    t57 = $[119];
  }
  let t58;
  if ($[120] !== t57) {
    t58 = <Typography variant="h6" component="h3" sx={t56}>{t57}</Typography>;
    $[120] = t57;
    $[121] = t58;
  } else {
    t58 = $[121];
  }
  let t59;
  if ($[122] !== LL) {
    t59 = LL.PRIVACY_COOKIES_PREFERENCES_BODY();
    $[122] = LL;
    $[123] = t59;
  } else {
    t59 = $[123];
  }
  let t60;
  if ($[124] !== t59) {
    t60 = <PrivacyParagraph>{t59}</PrivacyParagraph>;
    $[124] = t59;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  let t61;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = {
      mt: 3,
      mb: 1
    };
    $[126] = t61;
  } else {
    t61 = $[126];
  }
  let t62;
  if ($[127] !== LL) {
    t62 = LL.PRIVACY_COOKIES_ANALYTICS_TITLE();
    $[127] = LL;
    $[128] = t62;
  } else {
    t62 = $[128];
  }
  let t63;
  if ($[129] !== t62) {
    t63 = <Typography variant="h6" component="h3" sx={t61}>{t62}</Typography>;
    $[129] = t62;
    $[130] = t63;
  } else {
    t63 = $[130];
  }
  let t64;
  if ($[131] !== LL) {
    t64 = LL.PRIVACY_COOKIES_ANALYTICS_BODY();
    $[131] = LL;
    $[132] = t64;
  } else {
    t64 = $[132];
  }
  let t65;
  if ($[133] !== t64) {
    t65 = <PrivacyParagraph>{t64}</PrivacyParagraph>;
    $[133] = t64;
    $[134] = t65;
  } else {
    t65 = $[134];
  }
  let t66;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = {
      mt: 3,
      mb: 1
    };
    $[135] = t66;
  } else {
    t66 = $[135];
  }
  let t67;
  if ($[136] !== LL) {
    t67 = LL.PRIVACY_COOKIE_TABLE_TITLE();
    $[136] = LL;
    $[137] = t67;
  } else {
    t67 = $[137];
  }
  let t68;
  if ($[138] !== t67) {
    t68 = <Typography variant="h6" component="h3" sx={t66}>{t67}</Typography>;
    $[138] = t67;
    $[139] = t68;
  } else {
    t68 = $[139];
  }
  let t69;
  if ($[140] !== LL) {
    t69 = LL.PRIVACY_COOKIE_TABLE_INTRO();
    $[140] = LL;
    $[141] = t69;
  } else {
    t69 = $[141];
  }
  let t70;
  if ($[142] !== t69) {
    t70 = <PrivacyParagraph>{t69}</PrivacyParagraph>;
    $[142] = t69;
    $[143] = t70;
  } else {
    t70 = $[143];
  }
  let t71;
  if ($[144] !== LL) {
    t71 = <PrivacyCookieInventoryTable LL={LL} />;
    $[144] = LL;
    $[145] = t71;
  } else {
    t71 = $[145];
  }
  let t72;
  if ($[146] !== t48 || $[147] !== t50 || $[148] !== t53 || $[149] !== t55 || $[150] !== t58 || $[151] !== t60 || $[152] !== t63 || $[153] !== t65 || $[154] !== t68 || $[155] !== t70 || $[156] !== t71) {
    t72 = <PrivacySection title={t48}>{t50}{t53}{t55}{t58}{t60}{t63}{t65}{t68}{t70}{t71}</PrivacySection>;
    $[146] = t48;
    $[147] = t50;
    $[148] = t53;
    $[149] = t55;
    $[150] = t58;
    $[151] = t60;
    $[152] = t63;
    $[153] = t65;
    $[154] = t68;
    $[155] = t70;
    $[156] = t71;
    $[157] = t72;
  } else {
    t72 = $[157];
  }
  let t73;
  if ($[158] !== LL) {
    t73 = LL.PRIVACY_EXECUTION_TITLE();
    $[158] = LL;
    $[159] = t73;
  } else {
    t73 = $[159];
  }
  let t74;
  if ($[160] !== LL) {
    t74 = LL.PRIVACY_EXECUTION_BODY();
    $[160] = LL;
    $[161] = t74;
  } else {
    t74 = $[161];
  }
  let t75;
  if ($[162] !== t74) {
    t75 = <PrivacyParagraph>{t74}</PrivacyParagraph>;
    $[162] = t74;
    $[163] = t75;
  } else {
    t75 = $[163];
  }
  let t76;
  if ($[164] !== t73 || $[165] !== t75) {
    t76 = <PrivacySection title={t73}>{t75}</PrivacySection>;
    $[164] = t73;
    $[165] = t75;
    $[166] = t76;
  } else {
    t76 = $[166];
  }
  let t77;
  if ($[167] !== LL) {
    t77 = LL.PRIVACY_CCPA_TITLE();
    $[167] = LL;
    $[168] = t77;
  } else {
    t77 = $[168];
  }
  let t78;
  if ($[169] !== LL) {
    t78 = LL.PRIVACY_CCPA_BODY();
    $[169] = LL;
    $[170] = t78;
  } else {
    t78 = $[170];
  }
  let t79;
  if ($[171] !== t78) {
    t79 = <PrivacyParagraph>{t78}</PrivacyParagraph>;
    $[171] = t78;
    $[172] = t79;
  } else {
    t79 = $[172];
  }
  let t80;
  if ($[173] !== t77 || $[174] !== t79) {
    t80 = <PrivacySection title={t77}>{t79}</PrivacySection>;
    $[173] = t77;
    $[174] = t79;
    $[175] = t80;
  } else {
    t80 = $[175];
  }
  let t81;
  if ($[176] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = {
      mt: 4
    };
    $[176] = t81;
  } else {
    t81 = $[176];
  }
  let t82;
  if ($[177] !== LL) {
    t82 = LL.DASHBOARD();
    $[177] = LL;
    $[178] = t82;
  } else {
    t82 = $[178];
  }
  let t83;
  if ($[179] !== t82) {
    t83 = <Typography variant="body2" color="text.secondary" sx={t81}><MuiLink component={Link} href="/" underline="hover">{t82}</MuiLink></Typography>;
    $[179] = t82;
    $[180] = t83;
  } else {
    t83 = $[180];
  }
  let t84;
  if ($[181] !== t11 || $[182] !== t21 || $[183] !== t25 || $[184] !== t29 || $[185] !== t33 || $[186] !== t39 || $[187] !== t43 || $[188] !== t47 || $[189] !== t7 || $[190] !== t72 || $[191] !== t76 || $[192] !== t80 || $[193] !== t83 || $[194] !== t9) {
    t84 = <Container maxWidth="md" sx={t5}>{t7}{t9}{t11}{t21}{t25}{t29}{t33}{t39}{t43}{t47}{t72}{t76}{t80}{t83}</Container>;
    $[181] = t11;
    $[182] = t21;
    $[183] = t25;
    $[184] = t29;
    $[185] = t33;
    $[186] = t39;
    $[187] = t43;
    $[188] = t47;
    $[189] = t7;
    $[190] = t72;
    $[191] = t76;
    $[192] = t80;
    $[193] = t83;
    $[194] = t9;
    $[195] = t84;
  } else {
    t84 = $[195];
  }
  let t85;
  if ($[196] !== t4 || $[197] !== t84) {
    t85 = <MainLayout>{t4}{t84}</MainLayout>;
    $[196] = t4;
    $[197] = t84;
    $[198] = t85;
  } else {
    t85 = $[198];
  }
  return t85;
};
export default PrivacyPage;